import type { OpenAPI } from "simplydo/interfaces";
import type {
  IdeaWithAssessments,
  IdeaWithOwnerChallenge,
} from "simplydo/interfaces/types/ideaTypes";
import type { UiColor } from "simplydo/react/theme";

import { canEditIdea, hasPermission } from "./permissions";

export type AnonymityCheckExcludedItem =
  | { kind: "edi"; name: string }
  | { kind: "businessProfile"; name: string }
  | { kind: "managerSection"; name: string }
  | { kind: "managerField"; sectionName: string; fieldTitle: string };

export const getAnonymityCheckExcludedItems = (
  ideaTemplate: OpenAPI.Schemas["IdeaTemplate"] | undefined | null,
): AnonymityCheckExcludedItem[] => {
  if (!ideaTemplate?.body) {
    return [];
  }

  const items: AnonymityCheckExcludedItem[] = [];

  for (const section of ideaTemplate.body) {
    const sectionName = section.name?.trim() || "(Untitled section)";

    if (section.type === "edi") {
      items.push({ kind: "edi", name: sectionName });
      continue;
    }

    if (section.type === "businessProfile") {
      items.push({ kind: "businessProfile", name: sectionName });
      continue;
    }

    if (section.type === "assessment" || section.type === "impact") {
      continue;
    }

    if (section.visibility === "managers") {
      items.push({ kind: "managerSection", name: sectionName });
      continue;
    }

    for (const field of section.fields ?? []) {
      if (field.visibility === "manager") {
        items.push({
          kind: "managerField",
          sectionName,
          fieldTitle: field.title?.trim() || "(Untitled question)",
        });
      }
    }
  }

  return items;
};

export const shouldShowIdeaSection = (
  idea: IdeaWithAssessments | undefined,
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  if (!idea || !section) return false;

  if (section.type === "assessment") {
    return idea.ownerIdeaTemplate?.assessmentEnabled;
  }

  return (
    section.fields?.length > 0 ||
    section.type === "businessProfile" ||
    section.type === "impact" ||
    section.type === "edi"
  );
};

const NON_SELECTABLE_DEADLINE_FIELD_TYPES = new Set([
  "row",
  "infotext",
  "assessment",
  "assessmentFeedback",
]);

/** Non-templated idea fields that can be allowlisted on a submission deadline. */
export const DEADLINE_SPECIAL_FIELD_IDS = {
  name: "name",
  coverImage: "coverImage",
  businessProfile: "businessProfile",
  impactStatement: "impactStatement",
} as const;

const DEADLINE_SPECIAL_FIELDS: { id: string; titleKey: string }[] = [
  {
    id: DEADLINE_SPECIAL_FIELD_IDS.name,
    titleKey: "ideas.deadline.specialFields.name",
  },
  {
    id: DEADLINE_SPECIAL_FIELD_IDS.coverImage,
    titleKey: "ideas.deadline.specialFields.coverImage",
  },
  {
    id: DEADLINE_SPECIAL_FIELD_IDS.businessProfile,
    titleKey: "ideas.deadline.specialFields.businessProfile",
  },
  {
    id: DEADLINE_SPECIAL_FIELD_IDS.impactStatement,
    titleKey: "ideas.deadline.specialFields.impactStatement",
  },
];

export type DeadlineSelectableField = {
  id: string;
  title: string;
  sectionName: string;
};

type DeadlineLockIdea = {
  submissionDeadline?: Pick<
    NonNullable<OpenAPI.Schemas["Idea"]["submissionDeadline"]>,
    "editableFields"
  > | null;
  ownerChallenge?: { stage?: string | null } | null;
};

/**
 * Deadline field locks only apply once the challenge has closed; while it is open everyone can
 * edit their ideas freely, so locking individual fields would be meaningless.
 */
export const hasDeadlineFieldRestriction = (
  idea: DeadlineLockIdea | null | undefined,
): boolean =>
  idea?.ownerChallenge?.stage === "closed" &&
  !!idea?.submissionDeadline?.editableFields?.length;

export const isFieldEditableUnderDeadline = (
  idea: DeadlineLockIdea | null | undefined,
  fieldId: string,
  options?: { bypass?: boolean },
): boolean => {
  if (options?.bypass) return true;
  if (!hasDeadlineFieldRestriction(idea)) return true;
  return !!idea?.submissionDeadline?.editableFields?.includes(fieldId);
};

export const getDeadlineEditableFieldTitles = (
  template: OpenAPI.Schemas["IdeaTemplate"] | undefined | null,
  editableFieldIds: string[] | undefined | null,
  t: (key: string) => string,
): string[] => {
  if (!editableFieldIds?.length) return [];

  const specialTitleById = new Map(
    DEADLINE_SPECIAL_FIELDS.map((field) => [field.id, t(field.titleKey)]),
  );
  const templateTitleById = new Map<string, string>();
  for (const section of template?.body ?? []) {
    for (const field of section.fields ?? []) {
      if (field.id && field.title && !templateTitleById.has(field.id)) {
        templateTitleById.set(field.id, field.title);
      }
    }
  }

  return editableFieldIds
    .map(
      (fieldId) =>
        specialTitleById.get(fieldId) ??
        templateTitleById.get(fieldId) ??
        fieldId,
    )
    .filter(Boolean);
};

export const getSelectableDeadlineFields = (
  template: OpenAPI.Schemas["IdeaTemplate"] | undefined | null,
  t: (key: string) => string,
): DeadlineSelectableField[] => {
  const fields: DeadlineSelectableField[] = DEADLINE_SPECIAL_FIELDS.map(
    (field) => ({ id: field.id, title: t(field.titleKey), sectionName: "" }),
  );

  if (!template?.body) return fields;

  for (const section of template.body) {
    if (
      section.type === "assessment" ||
      section.type === "impact" ||
      section.type === "businessProfile"
    ) {
      continue;
    }
    for (const field of section.fields ?? []) {
      if (NON_SELECTABLE_DEADLINE_FIELD_TYPES.has(field.type)) continue;
      if (!field.id || !field.title) continue;
      fields.push({
        id: field.id,
        title: field.title,
        sectionName: section.name?.trim() || "",
      });
    }
  }
  return fields;
};

export const shouldShowIdeaEdiSection = (
  user: OpenAPI.Schemas["User"] | undefined,
  idea: IdeaWithOwnerChallenge | undefined,
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number],
) => {
  if (!idea || !section) return false;
  if (canEditIdea(user, idea)) return true;
  if (
    section.type === "edi" &&
    hasPermission(user, "org.viewEdi", idea.ownerChallenge?.organisation)
  )
    return true;
  return false;
};
